import React from 'react';
import { Box, Container, Typography, Stack, Link, TextField, Button, IconButton, useTheme, alpha, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import Logo from '../brand/Logo';
import Reveal from '../ui/Reveal';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const year = new Date().getFullYear();

  const col = (title: string, links: { label: string; to: string }[]) => (
    <Stack spacing={1.25} sx={{ minWidth: 140 }}>
      <Typography variant="overline" color="text.secondary">{title}</Typography>
      {links.map((l) => (
        <Link
          key={l.to + l.label}
          component={RouterLink}
          to={l.to}
          underline="none"
          color="text.primary"
          sx={{ fontWeight: 600, width: 'fit-content', position: 'relative', '&:hover': { color: 'primary.main' } }}
        >
          {l.label}
        </Link>
      ))}
    </Stack>
  );

  return (
    <Box component="footer" sx={{ position: 'relative', mt: { xs: 10, md: 16 } }}>
      {/* wave */}
      <Box
        aria-hidden
        component="svg"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        sx={{ display: 'block', width: '100%', height: { xs: 50, md: 90 }, fill: dark ? theme.palette.brand.paperDark : '#fff' }}
      >
        <path d="M0,64 C240,120 480,0 720,48 C960,96 1200,24 1440,72 L1440,120 L0,120 Z" />
      </Box>
      <Box sx={{ bgcolor: dark ? theme.palette.brand.paperDark : '#fff', pt: { xs: 4, md: 6 }, pb: 4 }}>
        <Container maxWidth="lg">
          <Reveal amount={0.1}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 5, md: 8 }} justifyContent="space-between">
              <Stack spacing={2.5} sx={{ maxWidth: 380 }}>
                <Logo height={40} />
                <Typography variant="body2" color="text.secondary">{t('footer.about')}</Typography>
                <Stack direction="row" spacing={0.5}>
                  {[InstagramIcon, XIcon, YouTubeIcon].map((Icon, i) => (
                    <IconButton
                      key={i}
                      size="small"
                      sx={{
                        color: 'text.primary', bgcolor: alpha(theme.palette.brand.lavender, dark ? 0.15 : 0.25),
                        '&:hover': { bgcolor: theme.palette.brand.lavender, color: theme.palette.brand.ink, transform: 'translateY(-3px)' },
                        transition: 'all .25s',
                      }}
                    >
                      <Icon fontSize="small" />
                    </IconButton>
                  ))}
                </Stack>
              </Stack>

              <Stack direction="row" spacing={{ xs: 5, md: 8 }} flexWrap="wrap" useFlexGap>
                {col(t('footer.platform'), [
                  { label: t('nav.learn'), to: '/learn' },
                  { label: t('nav.consultations'), to: '/consultations' },
                  { label: t('nav.community'), to: '/community' },
                  { label: t('nav.activities'), to: '/activities' },
                ])}
                {col(t('footer.support'), [
                  { label: t('footer.about_us'), to: '/' },
                  { label: t('footer.help'), to: '/' },
                  { label: t('footer.contact'), to: '/' },
                  { label: t('footer.privacy'), to: '/' },
                ])}
              </Stack>

              <Stack spacing={1.5} sx={{ maxWidth: 340 }}>
                <Typography variant="h6">{t('footer.newsletter_title')}</Typography>
                <Typography variant="body2" color="text.secondary">{t('footer.newsletter_text')}</Typography>
                <Stack direction="row" spacing={1} component="form" onSubmit={(e) => e.preventDefault()}>
                  <TextField size="small" placeholder={t('footer.newsletter_placeholder')} fullWidth />
                  <Button type="submit" variant="gradient" sx={{ whiteSpace: 'nowrap' }}>{t('footer.newsletter_button')}</Button>
                </Stack>
              </Stack>
            </Stack>
          </Reveal>

          <Divider sx={{ my: 4 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              © {year} {t('app.name')}. {t('footer.rights')}
            </Typography>
            <Typography variant="body2" color="text.secondary">🇴🇲 {t('app.oman')}</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
